import { Cookie as CookieNames, FeatureFlag } from '@ps/web-core-enums';
import { LoginSource } from '@ps/web-core-store';
import { DebugKey, createDebug } from '@ps/web-core-utils';
import * as Sentry from '@sentry/react';
import { countBy } from 'lodash';
import Cookies from 'universal-cookie';

import type { EffectParams } from '@swf/core/store';
import { getState, select } from '@swf/core/store';

import { loginSuccess } from './utils';

const debug = createDebug(DebugKey.Auth);
const cookies = new Cookies();
let authFailureCount = 0;

/**
 * Cookies can be set for all subdomains (.pokerstars.tld) or a specific sub-domain (www.pokerstars.tld).
 * SW uses the former when setting Cookies, however stale cookies, and cookies set by other applications can cause conflicts.
 * This function removes authentication cookies set on the subdomain level
 */
const removeSubdomainAuthCookies = () => {
  const isDisabled = select.feature.get(getState())[FeatureFlag.WPDisableRemoveSubdomainCookies];
  if (isDisabled) return;

  const cookieNameCounts = countBy(
    document.cookie.split(';').map((cookie) => cookie.split('=')[0]?.trim())
  );

  if (
    Math.max(
      cookieNameCounts[CookieNames.TOKEN],
      cookieNameCounts[CookieNames.SIGNATURE],
      cookieNameCounts[CookieNames.WEB_ID]
    ) > 1
  ) {
    const hostname = select.location.getHostname(getState());
    debug(`Duplicate cookies found - Removing from ${hostname}`);

    cookies.remove(CookieNames.TOKEN, { domain: hostname, path: '/' });
    cookies.remove(CookieNames.SIGNATURE, { domain: hostname, path: '/' });
    cookies.remove(CookieNames.WEB_ID, { domain: hostname, path: '/' });
  }
};

export const fetchJWT = async (
  payload: { token: string; signature: string; webId: string } | undefined,
  request: EffectParams['request']
) => {
  const { token, signature, webId } = payload || {};
  if (token && signature && webId) {
    const shouldOmitAuthorizationHeader =
      select.feature.get(getState())[FeatureFlag.WPDisableSignedTokenHeader];

    const domain = select.location.getCookieDomain(getState());
    cookies.set(CookieNames.TOKEN, token, { domain, path: '/', encode: (value) => value });
    cookies.set(CookieNames.SIGNATURE, signature, { domain, path: '/', encode: (value) => value });
    cookies.set(CookieNames.WEB_ID, webId, { domain, path: '/', encode: (value) => value });

    removeSubdomainAuthCookies();

    const { body } = await request<{ token: string }>({
      service: '/api/v1-preview/auth/session',
      http: true,
      headers: shouldOmitAuthorizationHeader
        ? undefined
        : { Authorization: `StarsWeb-SignedToken ${token}.${signature}` }
    });

    return body.token;
  }
  return undefined;
};

export const resetAuthFailureCount = () => {
  authFailureCount = 0;
};

const verifySession = async (
  _payload: undefined,
  { dispatch, request, rootState }: EffectParams
) => {
  const isExtSite = select.cms.isExternalPartnerSite(rootState);
  const isEmbedded = select.device.isEmbedded(rootState);
  const loginSource = LoginSource.VerifySession;

  try {
    if (isExtSite) {
      const { idpToken } = select.session.get(rootState);
      if (idpToken) await loginSuccess({ loginSource }, { dispatch });
    } else {
      const token = isEmbedded
        ? await fetchJWT(window?.starsweb?.auth, request)
        : select.session.get(rootState).token;

      if (!token) {
        return isEmbedded && select.session.get(rootState).token
          ? dispatch.auth.logout({ implicit: true })
          : dispatch.session.update({ authSource: 'client', serverAuth: false });
      }

      await loginSuccess({ token, loginSource }, { dispatch });
    }

    resetAuthFailureCount();
  } catch (error: unknown) {
    authFailureCount++;

    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'session-error',
      message: error instanceof Error ? error.message : 'Unknown error',
      data: error || {}
    });

    if (isEmbedded && authFailureCount === 3) {
      Sentry.captureException(
        new Error('Authentication failed after three attempts', { cause: error })
      );
    }

    dispatch.auth.logout({ implicit: true });

    /**
     * Calling login provides opportunity for the host application to supply new credentials or present a login screen
     * However, this often results in a loop of failed authentication attempts increasing load on the server
     */
    if (isEmbedded && authFailureCount <= 3) {
      window.starsweb?.auth.login();
    }
  }
};

export default verifySession;
